'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface HeroSectionProps {
  onSearch: (filters: SearchFilters) => void;
}

interface SearchFilters {
  brand: string;
  location: string;
  service: string;
}

const HeroSection = ({ onSearch }: HeroSectionProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [filters, setFilters] = useState<SearchFilters>({
    brand: '',
    location: '',
    service: ''
  });

  useEffect(() => {
    setIsHydrated(true);

    const detectLocation = async () => {
        try {
            const res = await fetch('/api/geo');
            if (res.ok) {
                const data = await res.json();
                if (data.city && data.region) {
                    setFilters(prev => ({
                        ...prev,
                        location: `${data.city}, ${data.region}`
                    }));
                }
            }
        } catch (e) {
            console.error('Failed to parse geo location', e);
        }
    };

    detectLocation();
  }, []);

  const brands = ['BMW', 'Lexus', 'Mercedes-Benz', 'Audi', 'Acura', 'Toyota', 'Honda'];
  const cities = [
    'San Francisco',
    'San Jose',
    'Oakland',
    'Palo Alto',
    'Berkeley',
    'Santa Clara',
    'Redwood City',
    'Sunnyvale',
    'San Mateo',
    'Fremont'
  ];
  const services = [
    'Oil Change',
    'Brake Service',
    'Engine Diagnostics',
    'Transmission Repair',
    'Suspension Work',
    'Battery Replacement',
    'AC Repair',
    'Tire Service',
    'Body Shop'
  ];

  const handleFilterChange = (key: keyof SearchFilters, value: string) => {
    if (!isHydrated) return;
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSearch = () => {
    if (!isHydrated) return;
    onSearch(filters);
  };

  return (
    <section className="relative bg-gradient-to-br from-primary via-primary to-secondary overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-accent rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent rounded-full blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32">
        <div className="text-center mb-12">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
            Stop Overpaying for Car Repairs.
          </h1>
          <p className="font-body text-lg sm:text-xl text-white max-w-3xl mx-auto leading-relaxed">
            Use AI to analyze costs, find authentic OEM parts, watch DIY guides, or connect with top-rated local mechanics—all in one place.
          </p>
        </div>

        {/* DIY Smart Banner Interceptor */}
        <Link href="/diy" className="group block max-w-5xl mx-auto mb-6 transform transition-all duration-300 hover:scale-[1.01]">
          <div className="relative overflow-hidden rounded-xl border border-accent/30 bg-accent/10 backdrop-blur-md p-4 sm:p-5 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-[0_0_20px_rgba(var(--color-accent),0.15)] group-hover:bg-accent/20 transition-smooth">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-accent to-transparent opacity-50 group-hover:opacity-100 transition-opacity" />
            <div className="flex items-center space-x-4">
              <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-full bg-accent text-primary font-bold shadow-inner">
                <Icon name="WrenchScrewdriverIcon" size={24} />
              </div>
              <div className="text-left">
                <h3 className="font-heading text-base sm:text-lg font-bold text-white mb-0.5">
                  Small problem? Try doing it yourself!
                </h3>
                <p className="font-body text-xs sm:text-sm text-white/80 max-w-xl">
                  Save hundreds on costly labor by following our AI-curated DIY Maintenance Guides & OEM Parts Finder before booking a shop.
                </p>
              </div>
            </div>
            <div className="flex-shrink-0 flex items-center space-x-2 text-accent font-heading font-bold text-sm bg-primary/40 px-4 py-2 rounded-lg border border-accent/20 group-hover:border-accent">
              <span>Explore DIY Guides</span>
              <Icon name="ArrowRightIcon" size={16} className="group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </Link>
        
        <div className="bg-card rounded-2xl shadow-elevation-xl p-6 sm:p-8 max-w-5xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div>
              <label htmlFor="brand" className="font-caption text-xs font-medium text-card-foreground mb-2 block">
                Vehicle Brand
              </label>
              <div className="relative">
                <Icon 
                  name="TruckIcon" 
                  size={18} 
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" 
                />
                <select
                  id="brand"
                  value={filters.brand}
                  onChange={(e) => handleFilterChange('brand', e.target.value)}
                  className="w-full rounded-lg border border-border bg-input pl-10 pr-4 py-3 text-sm text-text-primary transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                >
                  <option value="">Select Brand</option>
                  {brands.map(brand => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label htmlFor="location" className="font-caption text-xs font-medium text-card-foreground mb-2 block">
                Location
              </label>
              <div className="relative">
                <Icon 
                  name="MapPinIcon" 
                  size={18} 
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" 
                />
                <input
                  id="location"
                  type="text"
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                  placeholder="City or ZIP code"
                  className="w-full rounded-lg border border-border bg-input pl-10 pr-4 py-3 text-sm text-text-primary placeholder:text-muted-foreground transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                />
              </div>
            </div>

            <div>
              <label htmlFor="service" className="font-caption text-xs font-medium text-card-foreground mb-2 block">
                Service Type
              </label>
              <div className="relative">
                <Icon 
                  name="WrenchScrewdriverIcon" 
                  size={18} 
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" 
                />
                <select
                  id="service"
                  value={filters.service}
                  onChange={(e) => handleFilterChange('service', e.target.value)}
                  className="w-full rounded-lg border border-border bg-input pl-10 pr-4 py-3 text-sm text-text-primary transition-smooth focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                >
                  <option value="">Select Service</option>
                  {services.map(service => (
                    <option key={service} value={service}>{service}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={handleSearch}
              className="flex-1 flex items-center justify-center space-x-2 rounded-lg bg-primary border-2 border-primary px-8 py-4 font-body text-base font-medium text-primary-foreground shadow-elevation-sm transition-smooth hover:opacity-90 active:scale-[0.97]"
            >
              <Icon name="MagnifyingGlassIcon" size={20} />
              <span>Find Mechanics Near Me</span>
            </button>
            
            <Link
              href="/diy"
              className="flex-1 flex items-center justify-center space-x-2 rounded-lg bg-transparent border-2 border-accent px-8 py-4 font-body text-base font-bold text-accent shadow-elevation-sm transition-all hover:bg-accent hover:text-black active:scale-[0.97] group"
            >
              <Icon name="PlayCircleIcon" size={22} className="group-hover:animate-pulse" />
              <span>Watch DIY Repair Guides (Save $)</span>
            </Link>
          </div>
        </div>

        <div className="mt-12 flex flex-wrap items-center justify-center gap-8 text-white">
          <div className="flex items-center space-x-2">
            <Icon name="CheckBadgeIcon" size={24} variant="solid" className="text-accent" />
            <span className="font-body text-sm">Top Rated Mechanics</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="ShieldCheckIcon" size={24} variant="solid" className="text-accent" />
            <span className="font-body text-sm">Trusted Reviews</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="CurrencyDollarIcon" size={24} variant="solid" className="text-accent" />
            <span className="font-body text-sm">Price Transparency</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;